import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, IconButton, Paper, Stack, Tab, Tabs, TextField } from "@mui/material";
import { AsyncContentMui } from "components/Async-content/AsyncContentMui";
import { Drawer } from "components/Measure-data/Drawer/Drawer";
import dayjs from "dayjs";
import { useFormik } from "formik";
import { useFormatDateHook } from "hooks";
import { useDealDefaultCurrencySelect } from "store";
import { useDebtActions } from "store/actions-context";
import { DebtSegment } from "store/types/deal/debt";
import { getDisabledFields } from "utils/debt";

import { AmoritzationDrawerPrincipalSection } from "./AmoritzationDrawerPrincipalSection";
import { AmortizationDrawerDebtSection } from "./AmortizationDrawerDebtSection";
import { AmortizationDrawerInterestSection } from "./AmortizationDrawerInterestSection";
import { AmortizationDrawerPIKSection } from "./AmortizationDrawerPIKSection";
import { FormValues, validationSchema } from "./schemas";

interface AmortizationDrawerProps {
  open: boolean;
  segments?: DebtSegment[];
  newSegmentDate?: string | null;
  newSegmentId?: number;
  onClose: () => void;
  onApply: (segments: DebtSegment[], deal_title?: string) => void;
  loading: boolean;
  newDeal?: boolean;
  presetValues?: Partial<DebtSegment>;
  defaultLoanName?: string;
}

const defaultSegment: DebtSegment = {
  id: 0,
  deal_id: 0,
  deal_title: "",
  loan_amount: 0,
  start_date: dayjs().format(),
  maturity_date: dayjs().format(),
  payment_term: "fixed_principal",
  principal_indexation_payment: false,
  interest_based_on_principal: false,
  overriding_parent: false,
  is_facility: false,
  is_withdrawal: true,

  principal_frequency: "month",
  principal_first_payment_date: dayjs().format(),
  principal_reference_index: null,
  principal_reference_day: null,
  principal_link_percent: 0,
  balloon_payment: null,

  interest_frequency: "month",
  interest_first_payment_date: dayjs().format(),
  interest_reference_index: null,
  interest_reference_day: null,
  loan_reset_date_offset: null,
  loan_reset_frequency: null,
  loan_reset_date: null,
  interest_link_percent: 0,
  base_rate: 0,
  ceiling_rate: null,
  floor_rate: 0,
  float_type: "spread",
  interest_rate_fixing: null,
  day_count_convention: "actual_actual",
  commission_rate: null,

  loan_status_frequency: "quarter",
  loan_first_status_date: null,

  pik_base_rate: null,
  pik_capitalization_frequency: null,
  pik_first_capitalization_date: null,
  pik_reference_index: null,
  pik_reference_day: null,
  pik_reference_day_offset: null,
  pik_link_percent: 0,
  pik_floor_rate: 0,
  pik_ceiling_rate: null,
  pik_float_type: "fixed",
  pik_rate_fixing: null,
  pik_accumulated: null,
  interest_on_pik_principal: true,

  vat: null
};

interface TabLabelProps {
  index: number;
  showCloseButton: boolean;
  onRemove: (index: number) => void;
  segments: DebtSegment[];
}

const TabLabel: React.FC<TabLabelProps> = ({ index, showCloseButton, onRemove, segments }) => {
  const formatDate = useFormatDateHook();

  const { t } = useTranslation("debt");

  const getSegmentName = (index: number) => {
    if (segments.length === 1) {
      return t("segment.initial");
    }

    if (index === segments.length - 1) {
      return t("segment.current");
    }

    // For first segment and middle segments, show start date and next segment's start date
    const currentStartDate = formatDate({ date: segments[index].start_date });
    const nextStartDate = formatDate({ date: dayjs(segments[index + 1].start_date).subtract(1, "day") });

    if (index === 0) {
      return t("segment.initialRange", { start: currentStartDate, end: nextStartDate });
    }

    return t("segment.dateRange", { start: currentStartDate, end: nextStartDate });
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <span>{getSegmentName(index)}</span>
      {showCloseButton && (
        <CloseIcon
          sx={{
            fontSize: 16,
            cursor: "pointer",
            "&:hover": { color: "error.main" }
          }}
          onClick={(e) => {
            e.stopPropagation();
            onRemove(index);
          }}
        />
      )}
    </Box>
  );
};

export const AmortizationDrawer: React.FC<AmortizationDrawerProps> = ({
  open,
  segments,
  newSegmentDate,
  newSegmentId,
  onClose,
  onApply,
  loading,
  newDeal,
  presetValues,
  defaultLoanName
}) => {
  const { t } = useTranslation("debt");
  const [tabValue, setTabValue] = useState(0);
  const [innerSegments, setInnerSegments] = useState<DebtSegment[]>([]);
  const { getDebtIndexValueAction } = useDebtActions();
  const currency = useDealDefaultCurrencySelect();

  useEffect(() => {
    if (!open) return;
    if (newSegmentDate && newSegmentId && segments && segments.length > 0) {
      // Change value click
      const innerSegments = [...segments];
      const segmentToCopy = segments.find((segment) => segment.id === newSegmentId) as DebtSegment;
      if (segmentToCopy.start_date !== newSegmentDate) {
        innerSegments.push({
          ...segmentToCopy,
          id: 0,
          start_date: newSegmentDate,
          principal_first_payment_date: newSegmentDate,
          interest_first_payment_date: newSegmentDate
        });
      }
      innerSegments.sort((a, b) => a.start_date.localeCompare(b.start_date));
      const tabValue = innerSegments.findIndex((segment) => segment.start_date === newSegmentDate);
      setInnerSegments(innerSegments);
      setTabValue(tabValue);
    } else if (segments && segments.length > 0) {
      // Edit Click
      const sortedSegments = [...segments].sort((a, b) => a.start_date.localeCompare(b.start_date));
      setInnerSegments(sortedSegments);
      setTabValue(0);
    } else if (presetValues) {
      // New Debt with preset values
      setInnerSegments([{ ...defaultSegment, ...presetValues }]);
    } else {
      // New Debt
      setInnerSegments([defaultSegment]);
    }
  }, [open, segments, newSegmentDate, newSegmentId]);

  const initialValues = useMemo<FormValues>(() => {
    if (innerSegments && innerSegments[tabValue]) {
      return {
        ...innerSegments[tabValue],
        deal_title: defaultLoanName || "",
        is_facility: false,
        is_withdrawal: true,
        fees: [],
        interest_index_value: null,
        principal_index_value: null,
        pik_index_value: null,
        custom_payments_file: null,
        overriding_parent: true,
        payment_term: innerSegments[tabValue].payment_term
      };
    }
    return {
      ...defaultSegment,
      deal_title: defaultLoanName || (newDeal ? "Loan withdrawal" : ""),
      is_facility: false,
      is_withdrawal: true,
      fees: [],
      interest_index_value: null,
      principal_index_value: null,
      pik_index_value: null,
      custom_payments_file: null,
      overriding_parent: true,
      payment_term: defaultSegment.payment_term
    };
  }, [innerSegments, tabValue, newDeal, defaultLoanName]);

  const formik = useFormik<FormValues>({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      const { deal_title, ...rest } = values;

      // Update current segment in allSegments
      const updatedSegments = [...innerSegments];
      updatedSegments[tabValue] = {
        ...rest,
        id: innerSegments[tabValue].id,
        deal_id: innerSegments[tabValue].deal_id,
        deal_title: innerSegments[tabValue].deal_title
      };

      // if (formik.values.custom_payments_file) {
      //   const customPaymentsFile = formik.values.custom_payments_file;
      //   if (customPaymentsFile) {
      //     const formData = new FormData();
      //     formData.append("custom_payments_file", customPaymentsFile);
      //     formData.append("deal_id", String(dealId));
      //     const response = await uploadCustomPaymentsAction(formData);
      //   }
      // }

      onApply(updatedSegments, deal_title);
    }
  });

  const saveCurrentFormState = () => {
    const updatedSegments = [...innerSegments];
    updatedSegments[tabValue] = {
      ...formik.values,
      id: innerSegments[tabValue].id,
      deal_id: innerSegments[tabValue].deal_id,
      deal_title: innerSegments[tabValue].deal_title
    };

    return updatedSegments;
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    const updatedSegments = saveCurrentFormState();
    setInnerSegments(updatedSegments);
    setTabValue(newValue);
  };

  const handleAddSegment = () => {
    const updatedSegments = saveCurrentFormState();
    const newSegments = [
      ...updatedSegments,
      {
        ...formik.values,
        id: 0,
        deal_id: innerSegments[tabValue].deal_id,
        deal_title: innerSegments[tabValue].deal_title
      }
    ];
    setInnerSegments(newSegments);
    setTabValue(newSegments.length - 1);
  };

  const handleRemoveSegment = (index: number) => {
    const newSegments = [...innerSegments];
    newSegments.splice(index, 1);
    setInnerSegments(newSegments);
    setTabValue(Math.max(0, index - 1));
  };

  const updateIndexValue = async (index: string, date: string | null, type: "principal" | "interest" | "pik") => {
    const referenceDay = date || new Date().toISOString();
    try {
      const value = await getDebtIndexValueAction(index, referenceDay.split("T")[0]);
      formik.setFieldValue(`${type}_index_value`, value);
    } catch (error) {
      console.error("Failed to fetch index value:", error);
    }
  };

  const disabledFields = getDisabledFields({
    paymentTerm: formik.values.payment_term,
    isWithdrawal: true,
    isFacility: false,
    overridingParent: true
  });

  return (
    <Drawer open={open} handleClose={onClose} title={t("amortizationDrawer.title")}>
      <AsyncContentMui spinnerProps={{ size: 100 }} isLoading={loading}>
        <form
          onSubmit={formik.handleSubmit}
          style={{ height: "0", flexGrow: 1, display: "flex", flexDirection: "column" }}
        >
          {/* Tabs - Fixed at top */}
          <Box sx={{ px: 5 }}>
            <Box sx={{ borderBottom: 1, borderColor: "#DBE8F5", position: "relative" }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  pr: 5,
                  mb: "-1px",
                  "& .MuiTabs-indicator": {
                    zIndex: 1
                  }
                }}
              >
                {innerSegments.map((segment, index) => (
                  <Tab
                    key={`${segment.id} + ${index}`}
                    label={
                      <TabLabel
                        index={index}
                        showCloseButton={innerSegments.length > 1}
                        onRemove={handleRemoveSegment}
                        segments={innerSegments}
                      />
                    }
                  />
                ))}
              </Tabs>
              {segments && (
                <IconButton
                  size="sm"
                  sx={{ position: "absolute", right: 0, top: "50%", transform: "translateY(-50%)" }}
                  onClick={handleAddSegment}
                >
                  <AddIcon />
                </IconButton>
              )}
            </Box>
          </Box>

          <Stack
            spacing={5}
            sx={{
              p: 5,
              flex: 1,
              overflow: "auto"
            }}
          >
            {newDeal && (
              <TextField
                fullWidth
                name="deal_title"
                label={t("amortizationDrawer.loanName")}
                placeholder={t("amortizationDrawer.loanNamePlaceholder")}
                value={formik.values.deal_title || ""}
                onChange={formik.handleChange}
                error={formik.touched.deal_title && Boolean(formik.errors.deal_title)}
                helperText={formik.touched.deal_title && formik.errors.deal_title}
              />
            )}

            <AmortizationDrawerDebtSection formik={formik} currency={currency || "USD"} />

            <AmoritzationDrawerPrincipalSection
              formik={formik}
              updateIndexValue={updateIndexValue}
              disabledFields={disabledFields}
            />

            <AmortizationDrawerInterestSection
              formik={formik}
              updateIndexValue={updateIndexValue}
              disabledFields={disabledFields}
            />

            <AmortizationDrawerPIKSection
              formik={formik}
              updateIndexValue={updateIndexValue}
              disabledFields={disabledFields}
            />
          </Stack>

          {/* Footer */}
          <Paper
            elevation={8}
            sx={{
              p: 5,
              borderTop: "1px solid",
              borderColor: "divider",
              boxShadow: "0px -4px 8px rgba(0, 0, 0, 0.1)",
              display: "flex",
              justifyContent: "flex-end",
              gap: 5,
              bgcolor: "background.paper",
              zIndex: 1
            }}
          >
            <Button variant="outlined" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button variant="contained" type="submit">
              {t("common.apply")}
            </Button>
          </Paper>
        </form>
      </AsyncContentMui>
    </Drawer>
  );
};
